import React from 'react';
import Icon from '../../../components/AppIcon';


const MessageTimeline = ({ messages, currentLanguage, onMessageClick }) => {
  const labels = {
    fr: {
      title: 'Communications récentes',
      noMessages: 'Aucune communication',
      recipients: 'destinataires',
      delivered: 'Livré',
      pending: 'En attente',
      failed: 'Échec',
      read: 'Lu',
      sent: 'Envoyé',
      scheduled: 'Planifié',
      viewDetails: 'Voir les détails'
    },
    ar: {
      title: 'الاتصالات الأخيرة',
      noMessages: 'لا توجد اتصالات',
      recipients: 'مستلم',
      delivered: 'تم التسليم',
      pending: 'قيد الانتظار',
      failed: 'فشل',
      read: 'مقروء',
      sent: 'مرسل',
      scheduled: 'مجدول',
      viewDetails: 'عرض التفاصيل'
    }
  };

  const getMessageTypeIcon = (type) => {
    const icons = {
      email: 'Mail',
      whatsapp: 'MessageCircle',
      sms: 'Smartphone'
    };
    return icons?.[type] || 'Mail';
  };

  const getStatusConfig = (status) => {
    const configs = {
      delivered: {
        icon: 'CheckCheck',
        color: 'text-success',
        bgColor: 'bg-success/10',
        label: labels?.[currentLanguage]?.delivered
      },
      pending: {
        icon: 'Clock',
        color: 'text-warning',
        bgColor: 'bg-warning/10',
        label: labels?.[currentLanguage]?.pending
      },
      failed: {
        icon: 'XCircle',
        color: 'text-error',
        bgColor: 'bg-error/10',
        label: labels?.[currentLanguage]?.failed
      },
      read: {
        icon: 'Eye',
        color: 'text-primary',
        bgColor: 'bg-primary/10',
        label: labels?.[currentLanguage]?.read
      },
      sent: {
        icon: 'Send',
        color: 'text-accent',
        bgColor: 'bg-accent/10',
        label: labels?.[currentLanguage]?.sent
      },
      scheduled: {
        icon: 'Calendar',
        color: 'text-secondary',
        bgColor: 'bg-secondary/10',
        label: labels?.[currentLanguage]?.scheduled
      }
    };
    return configs?.[status] || configs?.pending;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return currentLanguage === 'fr' ? 'À l\'instant' : 'الآن';
    if (diffMins < 60) return `${diffMins} ${currentLanguage === 'fr' ? 'min' : 'د'}`;
    if (diffHours < 24) return `${diffHours} ${currentLanguage === 'fr' ? 'h' : 'س'}`;
    if (diffDays < 7) return `${diffDays} ${currentLanguage === 'fr' ? 'j' : 'ي'}`;

    // Always use en-US locale for universal date format (Western numerals)
    return date?.toLocaleDateString('en-US');
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-warm p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="MessageSquare" size={20} className="text-accent" />
          </div>
          <h2 className="text-lg md:text-xl font-semibold text-foreground">
            {labels?.[currentLanguage]?.title}
          </h2>
        </div>
      </div>
      {messages?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">{labels?.[currentLanguage]?.noMessages}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {messages?.map((message) => {
            const statusConfig = getStatusConfig(message?.status);
            return (
              <div
                key={message?.id}
                className="border border-border rounded-lg p-4 hover:shadow-warm transition-smooth cursor-pointer"
                onClick={() => onMessageClick?.(message)}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name={getMessageTypeIcon(message?.type)} size={20} className="text-primary" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-foreground truncate">
                          {message?.subject}
                        </h3>
                        <p className="caption text-muted-foreground mt-1">
                          {message?.recipientCount} {labels?.[currentLanguage]?.recipients}
                        </p>
                      </div>
                      <span className="caption text-muted-foreground flex-shrink-0">
                        {formatTimestamp(message?.timestamp)}
                      </span>
                    </div>

                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                      {message?.preview}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${statusConfig?.bgColor}`}>
                        <Icon name={statusConfig?.icon} size={14} className={statusConfig?.color} />
                        <span className={`caption font-medium ${statusConfig?.color}`}>
                          {statusConfig?.label}
                        </span>
                      </div>

                      {message?.responseRate !== undefined && (
                        <div className="flex items-center gap-2 caption text-muted-foreground">
                          <Icon name="TrendingUp" size={14} />
                          <span>{message?.responseRate}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MessageTimeline;